import { makeStyles } from '@mui/styles';
import { IconButton, Tooltip } from 'apollo-react';
import Paper from 'apollo-react/components/Paper';
import Popover from 'apollo-react/components/Popover';
import TextField from 'apollo-react/components/TextField';
import OpenNew from 'apollo-react-icons/OpenNew';
import React, { useEffect, useRef, useState } from 'react';

import RuntimeTranslation from '../../../components/RuntimeTranslation';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getUITA } from '../../../utils/commonUtils';
import { showAsteriskBefore } from '../../utils/form-manager.utils';

const useStyles = makeStyles({
  readOnly: {
    backgroundColor: '#f2f2f2',
    color: '#666666',

    '&:hover': {
      borderColor: '#d9d9d9',
    },
    '&:focus': {
      boxShadow: 'unset !important',
      borderColor: '#d9d9d9 !important',
    },
  },
  popIconBackground: {
    background: '#ffffff',
  },
});

const InTableTextArea = ({
  label,
  actions,
  value = '',
  isHiddenLabel,
  isRequired,
  className,
  error,
  maxLength,
  instanceId,
  entityName,
  fieldName,
  isEditor,
  disabled,
  isInComplexGridRow,
  isRunTimeTranslation = true,
  paperMaxWidth = 600,
  nuberOfRows = 4,
  paperTextFieldWidth = 460,
}) => {
  const [newValue, setValue] = useState(value);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const textBoxRef = useRef(null);
  const expandedRef = useRef(null);
  const translateString = useTranslation();
  const classes = useStyles();

  //update inner state when `value` is changed from outside
  useEffect(() => {
    setValue(value);
  }, [value]);

  const onBlur = ({ target: { value: v } }) => {
    const _value = v.trim();
    setValue(_value);

    if (value !== _value) {
      actions.updateControlValue(_value);
    }
  };

  const openPopover = () => {
    setPopoverAnchor(textBoxRef.current);
    setTimeout(() => {
      expandedRef.current.focus();
    }, 200);
  };

  const isReadOnly = isInComplexGridRow || isEditor;

  return (
    <div
      style={{ display: 'flex' }}
      ui-ta={getUITA('FormCtrl', 'SSTextArea', entityName, fieldName)}
    >
      <TextField
        ref={textBoxRef}
        id={instanceId}
        label=""
        fullWidth
        asteriskPosition="before"
        value={newValue ?? ''}
        className={className}
        onBlur={onBlur}
        onClick={openPopover}
        multiline
        onChange={({ target: { value } }) => setValue(value)}
        required={isRequired}
        error={error}
        inputProps={{
          maxLength,
          style: {
            height: 40,
            minHeight: 40,
            overflow: 'hidden',
          },
          readOnly: isReadOnly,
          className: isReadOnly ? classes.readOnly : null,
        }}
        disabled={disabled}
        style={{ minWidth: 184 }}
        icon={
          <Tooltip title={translateString('Expand')}>
            <div className={classes.popIconBackground}>
              <IconButton size="small" aria-label="Popover">
                <OpenNew />
              </IconButton>
            </div>
          </Tooltip>
        }
        iconProps={{
          onClick: openPopover,
        }}
      />
      <Popover
        open={!!popoverAnchor}
        anchorEl={popoverAnchor}
        onClose={() => setPopoverAnchor(null)}
      >
        <Paper style={{ padding: '0 16px 16px 16px', maxWidth: paperMaxWidth, display: 'flex' }}>
          <TextField
            id={instanceId}
            label={isHiddenLabel ? '' : label}
            fullWidth
            asteriskPosition={showAsteriskBefore(label, false) ? 'before' : 'after'}
            multiline
            value={newValue ?? ''}
            className={className}
            onBlur={onBlur}
            onChange={({ target: { value } }) => setValue(value)}
            required={isRequired}
            error={error}
            sizeAdjustable
            inputProps={{
              rows: nuberOfRows,
              maxLength,
              ref: expandedRef,
              style: {
                height: 'auto',
                width: paperTextFieldWidth,
                overflow: 'auto',
                resize: 'both',
              },
              className: isReadOnly ? classes.readOnly : null,
              readOnly: isReadOnly,
              'aria-label': label,
            }}
            disabled={disabled}
          />
          {isRunTimeTranslation && <RuntimeTranslation value={newValue} />}
        </Paper>
      </Popover>
    </div>
  );
};

export default InTableTextArea;
